import React, { useState } from "react";
import type { Record } from "react-admin";
import { Show, SimpleShowLayout, TextField, FunctionField } from "react-admin";
import { useInventoryTypesBlacklist } from "~/hooks/inventory.tsx";
import ColourContainerTable from "./colour-container-table.tsx";
import { formatInventoryItemFullValue } from "./format-inventory-item-value.ts";
import InventoryItemValueField from "./inventory-item-value-field.tsx";
import Button from "@material-ui/core/Button";
import CodeIcon from "@material-ui/icons/Code";
import TimezoneDateField from "~/components/date-field.tsx";

function BaseInventoryItemShow({ colours, containers, ...props }: any) {
	const { allowInventoryType } = useInventoryTypesBlacklist();
	const [showRawError, setShowRawError] = useState(false);

	return (
		<Show {...props}>
			<SimpleShowLayout>
				<TextField source="id" />
				<TimezoneDateField source="createdAt" showTime />

				<TextField source="reason" />
				<TextField source="isLoss" />
				<TextField source="reversalOfId" label="Reversal of" />
				<TextField source="xeroSyncing.status" label="Xero Sync Status" />
				<TimezoneDateField
					source="xeroSyncing.time"
					showTime
					label="Xero Sync Time"
				/>
				<FunctionField
					source="xeroSyncing.notes"
					label="Xero Sync Errors"
					render={(record?: Record) => {
						if (!record?.xeroSyncing?.notes) {
							return null;
						}

						const errorMessage = record.xeroSyncing.notes;
						const jsonStart = errorMessage.indexOf("{");
						if (jsonStart === -1) {
							return (
								<div style={{ whiteSpace: "pre-wrap" }}>{errorMessage}</div>
							);
						}

						try {
							// Extract the JSON part of the message
							const jsonPart = errorMessage.substring(jsonStart);
							const errorData = JSON.parse(jsonPart);

							// If it's a validation error, extract the relevant messages
							const validationErrors =
								errorData.response?.body?.Elements?.[0]?.ValidationErrors;
							if (validationErrors) {
								return (
									<div>
										<div style={{ whiteSpace: "pre-wrap" }}>
											{validationErrors.map((error: any, index: number) => (
												<div
													key={index}
													style={{ color: "red", marginBottom: "8px" }}
												>
													{error.Message}
												</div>
											))}
										</div>
										<Button
											startIcon={<CodeIcon />}
											onClick={() => setShowRawError(!showRawError)}
											variant="outlined"
											color="primary"
											size="medium"
											style={{
												marginTop: "8px",
												borderWidth: "2px",
												fontWeight: "bold",
												backgroundColor: showRawError ? "#e3f2fd" : "white",
											}}
										>
											{showRawError ? "Hide Raw Error" : "Show Raw Error"}
										</Button>
										{showRawError && (
											<pre
												style={{
													whiteSpace: "pre-wrap",
													backgroundColor: "#f5f5f5",
													padding: "8px",
													marginTop: "8px",
													borderRadius: "4px",
													fontSize: "12px",
												}}
											>
												{errorMessage}
											</pre>
										)}
									</div>
								);
							}

							// If it's a different type of error, show the error message
							const responseBodyMessage = errorData.response?.body?.Message;
							if (responseBodyMessage) {
								return (
									<div>
										<div style={{ whiteSpace: "pre-wrap", color: "red" }}>
											{responseBodyMessage}
										</div>
										<Button
											startIcon={<CodeIcon />}
											onClick={() => setShowRawError(!showRawError)}
											variant="outlined"
											color="primary"
											size="medium"
											style={{
												marginTop: "8px",
												borderWidth: "2px",
												fontWeight: "bold",
												backgroundColor: showRawError ? "#e3f2fd" : "white",
											}}
										>
											{showRawError ? "Hide Raw Error" : "Show Raw Error"}
										</Button>
										{showRawError && (
											<pre
												style={{
													whiteSpace: "pre-wrap",
													backgroundColor: "#f5f5f5",
													padding: "8px",
													marginTop: "8px",
													borderRadius: "4px",
													fontSize: "12px",
												}}
											>
												{errorMessage}
											</pre>
										)}
									</div>
								);
							}

							// If we can't extract a specific message, show the formatted JSON
							return (
								<div>
									<pre style={{ whiteSpace: "pre-wrap" }}>
										{JSON.stringify(errorData, null, 2)}
									</pre>
									<Button
										startIcon={<CodeIcon />}
										onClick={() => setShowRawError(!showRawError)}
										variant="outlined"
										color="primary"
										size="medium"
										style={{
											marginTop: "8px",
											borderWidth: "2px",
											fontWeight: "bold",
											backgroundColor: showRawError ? "#e3f2fd" : "white",
										}}
									>
										{showRawError ? "Hide Raw Error" : "Show Raw Error"}
									</Button>
									{showRawError && (
										<pre
											style={{
												whiteSpace: "pre-wrap",
												backgroundColor: "#f5f5f5",
												padding: "8px",
												marginTop: "8px",
												borderRadius: "4px",
												fontSize: "12px",
											}}
										>
											{errorMessage}
										</pre>
									)}
								</div>
							);
						} catch {
							// If it's not valid JSON, just show the original message
							return (
								<div style={{ whiteSpace: "pre-wrap" }}>{errorMessage}</div>
							);
						}
					}}
				/>
				<FunctionField
					{...props}
					source="containers"
					render={(record?: Record) => {
						if (!record) {
							return <div />;
						}
						const { containers: containerItems } = record;
						if (!containerItems) {
							return <div />;
						}

						return (
							<ColourContainerTable
								containers={containers}
								colours={colours}
								renderCell={(colour, container) => {
									const item = containerItems.find(
										(i: any) =>
											i.colourId.toString() === colour.id.toString() &&
											i.containerId === container.identifier,
									);
									if (item === undefined) {
										return undefined;
									}
									return formatInventoryItemFullValue(record, item.value);
								}}
							/>
						);
					}}
				/>

				<InventoryItemValueField source="packaging" />
				<InventoryItemValueField source="brickSeparators" />
				<InventoryItemValueField source="smallFlatpackBox" />
				<InventoryItemValueField source="logoTile" />
				{allowInventoryType({ type: "paddedEnvelope" }) && (
					<InventoryItemValueField source="paddedEnvelope" />
				)}
				{allowInventoryType({ type: "singleBox" }) && (
					<InventoryItemValueField source="singleBox" />
				)}
				{allowInventoryType({ type: "doubleBox" }) && (
					<InventoryItemValueField source="doubleBox" />
				)}
				{allowInventoryType({ type: "tripleBox" }) && (
					<InventoryItemValueField source="tripleBox" />
				)}
				{allowInventoryType({ type: "paperA5" }) && (
					<InventoryItemValueField source="paperA5" label="Paper A5" />
				)}
				{allowInventoryType({ type: "paperA4" }) && (
					<InventoryItemValueField source="paperA4" label="Paper A4" />
				)}
				{allowInventoryType({ type: "paperLetter" }) && (
					<InventoryItemValueField source="paperLetter" label="Paper Letter" />
				)}
				{allowInventoryType({ type: "paperA3" }) && (
					<InventoryItemValueField source="paperA3" label="Paper A3" />
				)}
				<FunctionField
					{...props}
					source="baseplates"
					render={(record?: Record) => {
						if (!record) {
							return <div />;
						}
						const { baseplates: baseplateItems } = record;
						if (!baseplateItems) {
							return <div />;
						}

						return (
							<div>
								{baseplateItems
									.filter((item: any) => item.value !== null)
									.map(
										(item: any) =>
											`${item.size}: ${formatInventoryItemFullValue(
												record,
												item.value,
											)}`,
									)
									.join(", ")}
							</div>
						);
					}}
				/>
			</SimpleShowLayout>
		</Show>
	);
}

export default BaseInventoryItemShow;
